import { useNavigate } from "@tanstack/react-router";

import GeneralBg from "@/assets/img/modal/general_background.webp";
import GiftImage from "@/assets/img/modal/gift_image.webp";
import GiftEllipse from "@/assets/img/modal/gift_image_ellipse.webp";
import { advisorsList } from "@/features/advisors";
import { useChat } from "@/features/chat";
import { WebappRoutes } from "@/shared/lib/webapp-routes";

import type { AppModalComponentProps } from "../config/modal-registry";
import { markFree3MinutesGiftShown } from "../persistence/free-3-minutes-gift-seen";

const AARAV =
  advisorsList.find((a) => a.name === "Aarav Devraj") ?? advisorsList[0];

/**
 * First-entry free-minutes gift sheet (Figma: First_Entry_Gift, 3114:10569).
 * Bottom sheet over a blurred app scrim; dotted ellipse rotates clockwise.
 */
export function Free3MinutesGiftModal({ onClose }: AppModalComponentProps) {
  const navigate = useNavigate();
  const { setSelectedAdvisor } = useChat();

  const handleRedeem = () => {
    markFree3MinutesGiftShown();
    setSelectedAdvisor(AARAV);
    navigate({ to: WebappRoutes.READINGS });
    onClose();
  };

  return (
    <div
      data-slot="free-3-minutes-gift-modal"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      <div className="relative flex w-full flex-col items-center gap-4 overflow-hidden rounded-t-3xl bg-dark-surface px-4 pb-4 pt-6">
        <img
          src={GeneralBg}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-60 w-full select-none object-cover"
        />

        <div className="relative z-[1] flex w-full flex-col items-center gap-2">
          <div className="relative size-[220px] shrink-0">
            <img
              src={GiftImage}
              alt=""
              className="pointer-events-none absolute inset-0 size-full object-cover"
            />
            <img
              src={GiftEllipse}
              alt=""
              aria-hidden
              className="pointer-events-none absolute inset-[6px] size-[208px] animate-spin object-contain [animation-duration:20s]"
            />
          </div>

          <div className="flex items-center rounded-[15px] bg-green/20 px-3 py-2">
            <p className="font-body text-[10px] font-medium uppercase text-green">
              You’ve Received a Personal BONUS
            </p>
          </div>

          <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
            FREE 3 MINUTES GIFT
          </h2>

          <p className="w-full text-center font-body text-base text-purple-light/70">
            We’ve unlocked a{" "}
            <span className="font-medium text-purple-light">
              free 3-minute session
            </span>{" "}
            for you. Ask your question and get guidance from an astrologer.
          </p>
        </div>

        <button
          type="button"
          onClick={handleRedeem}
          className="relative z-[1] flex h-14 w-full shrink-0 items-center justify-center rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
        >
          Redeem now
        </button>
      </div>
    </div>
  );
}
